import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import Input from "../../Components/Input";
import Label from "../../Components/Label";
import FlexCentered from "../../Containers/FlexCentered";
import Button from "../../Components/Button";
import Dropdown from "../../Components/Dropdown";
import Address from "../../Components/Address";
import Heading from "../../Components/Heading";
import RenderedErrors from "../../Components/RenderedErrors";
import PostcodeLookup from "../../Components/PostcodeLookup";

function AddressInput({ value, onChange, darkMode }) {
    const [postcode, setPostcode] = useState(value?.postcode || "");
    const [formattedPostcode, setFormattedPostcode] = useState(
        value?.postcode || null,
    );
    const [selectedAddress, setSelectedAddress] = useState(
        value?.selectedAddress || null,
    );
    const [formattedAddress, setFormattedAddress] = useState(
        value?.formattedAddress || null,
    );
    const [location, setLocation] = useState(value?.location || null);
    const [addresses, setAddresses] = useState([]);
    const [customError, setCustomError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const controllerRef = useRef(null);
    const postcodeRef = useRef(null);

    const apiError = customError;

    const formatAddressLabel = (address) =>
        [
            address?.line_1,
            address?.line_2,
            address?.line_3,
            address?.post_town,
            address?.county,
            address?.postcode,
        ]
            .filter(Boolean)
            .join(", ");

    const toFormattedAddressArray = (address) =>
        [
            address?.line_1,
            address?.line_2,
            address?.line_3,
            address?.post_town,
            address?.county,
            address?.postcode,
        ].filter(Boolean);

    useEffect(() => {
        if (!value) return;
        setPostcode(value.postcode || "");
        setFormattedPostcode(value.postcode || null);
        setSelectedAddress(value.selectedAddress || null);
        setFormattedAddress(value.formattedAddress || null);
        setLocation(value.location || null);
    }, [value]);

    useEffect(() => {
        const handleEnterKey = (e) => {
            if (e?.key !== "Enter") return;
            if (!postcodeRef?.current?.contains(e.target)) return;
            e?.preventDefault();
            handleFind();
        };
        document.addEventListener("keypress", handleEnterKey);
        return () => document.removeEventListener("keypress", handleEnterKey);
    });

    const handleAddressSelected = (address) => {
        if (!address) {
            setIsLoading(false);
            onChange(null);
            return;
        }

        const newPostcode = address.postcode || formattedPostcode || postcode;
        const newAddress = toFormattedAddressArray(address);
        const newLocation =
            address.longitude != null && address.latitude != null
                ? {
                      type: "Point",
                      coordinates: [address.longitude, address.latitude],
                  }
                : location || null;

        setPostcode(newPostcode);
        setFormattedPostcode(newPostcode);
        setSelectedAddress(address);
        setFormattedAddress(newAddress);
        setLocation(newLocation);
        setCustomError(null);
        setIsLoading(false);
        onChange({
            postcode: newPostcode,
            formattedAddress: newAddress,
            location: newLocation,
            selectedAddress: address,
        });
    };

    const handleFind = (e) => {
        e?.preventDefault();
        setCustomError(null);
        setSelectedAddress(null);
        setFormattedAddress(null);
        setAddresses([]);
        onChange(null);

        const val = (postcodeRef?.current?.value || postcode).replace(
            /\s/g,
            "",
        );
        const primaFacieValid =
            /^[a-z0-9]*$/i.test(val) && val.length >= 5 && val.length <= 7;
        if (!primaFacieValid) {
            setIsLoading(false);
            return setCustomError("Invalid postcode");
        }

        const formatted = val.toUpperCase();
        setPostcode(formatted);
        setFormattedPostcode(null);
        setIsLoading(true);
        controllerRef.current
            ?.executeSearch(formatted)
            ?.catch((error) => {
                setCustomError(error?.message || "Address not found");
            })
            ?.finally(() => {
                setIsLoading(false);
            });
    };

    const handleAddressesRetrieved = (retrievedAddresses) => {
        setAddresses(retrievedAddresses || []);
        if (!retrievedAddresses?.length) {
            setCustomError("Address not found");
            onChange(null);
        }
    };

    const handleSearchCompleted = (error, retrievedAddresses) => {
        if (error || !retrievedAddresses?.length) {
            setCustomError(error?.message || "Address not found");
            setSelectedAddress(null);
            setFormattedAddress(null);
            onChange(null);
        }
    };

    const handleSearchError = (error) => {
        setCustomError(error?.message || "Address not found");
    };

    const handleReady = (controller) => {
        controllerRef.current = controller;
    };

    const selectAddressConfig = {
        data: addresses,
        getLabel: (address) => formatAddressLabel(address),
        getKey: (address) =>
            [
                address?.line_1,
                address?.line_2,
                address?.line_3,
                address?.post_town,
                address?.county,
                address?.postcode,
            ]
                .filter(Boolean)
                .join(""),
    };

    const labelClasses = (className) =>
        classNames(className, {
            "text-hgCream-50": darkMode,
        });

    const handleSelectAddress = (address) => {
        const newAddress = toFormattedAddressArray(address);
        if (!newAddress.length) {
            setSelectedAddress(address);
            setFormattedAddress(null);
            onChange({
                postcode: formattedPostcode || postcode,
                location,
                selectedAddress: address,
            });
            return;
        }

        const newPostcode = address.postcode || formattedPostcode || postcode;
        const newLocation =
            address.longitude != null && address.latitude != null
                ? {
                      type: "Point",
                      coordinates: [address.longitude, address.latitude],
                  }
                : location || null;

        setSelectedAddress(address);
        setFormattedPostcode(newPostcode);
        setFormattedAddress(newAddress);
        setLocation(newLocation);
        onChange({
            postcode: newPostcode,
            formattedAddress: newAddress,
            location: newLocation,
            selectedAddress: address,
        });
    };

    return (
        <FlexCentered
            col
            className={classNames({
                "pb-24": !formattedAddress,
                "pb-8": formattedAddress,
            })}
        >
            <PostcodeLookup
                apiKey="ak_mpp0qd3xNlDG8tdC3neGN2zyi69AV"
                onReady={handleReady}
                onAddressesRetrieved={handleAddressesRetrieved}
                onSearchCompleted={handleSearchCompleted}
                onAddressSelected={handleAddressSelected}
                onSearchError={handleSearchError}
            />
            <Label htmlFor="postcode" className={labelClasses("mb-2")}>
                Postcode
            </Label>
            <div className="grid grid-cols-[1fr_auto] w-[20rem] max-w-full mb-4">
                <Input
                    type="text"
                    id="postcode"
                    name="postcode"
                    autoComplete="off"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    inputMode="text"
                    innerRef={postcodeRef}
                    value={postcode}
                    onChange={(e) => {
                        const nextValue = e.target.value.toUpperCase();
                        setPostcode(nextValue);
                    }}
                />
                <Button
                    primary
                    type="button"
                    className="h-full w-[auto]"
                    onClick={handleFind}
                    loading={isLoading}
                >
                    Find
                </Button>
            </div>
            {apiError && (
                <RenderedErrors
                    errors={{}}
                    apiError={apiError}
                    className="bg-hgCream-50 mb-4"
                />
            )}
            {selectAddressConfig.data &&
                selectAddressConfig.data.length > 0 &&
                !apiError && (
                    <Dropdown
                        value={selectedAddress}
                        onChange={handleSelectAddress}
                        config={selectAddressConfig}
                        maxHeight="max-h-32"
                        className="w-[20rem] max-w-full mb-4"
                        placeholder="Select address"
                    />
                )}
            {formattedAddress && (
                <div className="bg-hgCream-50 px-8 py-4 rounded-lg w-[20rem] max-w-full">
                    <Heading tertiary className="text-center mb-1">
                        Address
                    </Heading>
                    <div className="border-b-2 mb-2" />
                    <Address formattedAddress={formattedAddress} />
                </div>
            )}
        </FlexCentered>
    );
}

export default AddressInput;
